export interface SafelyFetchGraphQl {
  url: string
  body: string
  returnOnError?: unknown[]
  /**
   * When `true`, GraphQL-level errors and shape mismatches are swallowed
   * silently. Use for queries gated behind pending indexer proposals where
   * a "field not found" response is the expected steady state.
   */
  silent?: boolean
}

export type SafelyFetchGraphQlFn = (params: SafelyFetchGraphQl) => Promise<unknown>

export const safelyFetchGraphQl = async ({
  url,
  body,
  returnOnError = [],
  silent = false,
}: SafelyFetchGraphQl) => {
  const logError = (...args: unknown[]) => {
    if (silent) return
    console.error(...args)
  }

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "*/*" },
      body,
      mode: "cors",
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => "<unreadable body>")
      logError(
        "[safelyFetchGraphQl] Non-OK response",
        url,
        response.status,
        response.statusText,
        errorText.slice(0, 500),
      )
      throw new Error(`HTTP ${response.status} ${response.statusText}`)
    }

    const data = (await response.json()) as unknown

    if (typeof data !== "object" || !data) {
      logError("[safelyFetchGraphQl] Response was not an object", url, data)
      throw new Error("Invalid response shape (non-object)")
    }

    if ("errors" in data && Array.isArray((data as { errors: unknown[] }).errors)) {
      logError("[safelyFetchGraphQl] GraphQL errors", url, (data as { errors: unknown[] }).errors)
    }

    if ("data" in data) {
      return (data as { data: unknown }).data
    }

    logError(
      "[safelyFetchGraphQl] Response missing `data` field",
      url,
      Object.keys(data),
    )
    throw new Error("Invalid response shape (missing data field)")
  } catch (error) {
    logError("[safelyFetchGraphQl] Fetch failed", url, error)
    return returnOnError
  }
}
